import { getMountedApps } from 'single-spa';
import { fetchMenuItems } from '../../../utilities/api';
import buildTranslator from '../../../utilities/translator';
import { SIDEBAR_CLASS } from '../../../assets/styles/style-helpers';
import { isLoggedIn } from '../../../utilities/helpers';

/**
 * Adds the event handlers for events potentially dispatched by the application.
 * @param {string} appSlug The current app slug.
 */
 const _addEventHandlers = (appConfig, appSlug) => {
    if (!window.$wx) { return; }
    // We're using an IIFE here because we're not sure if `addEventListener` properly supports async/await
    window.addEventListener(window.$wx.mfeLoginCompleteEvent?.type, () => {
        (async () => await _setMenuItems(appConfig, appSlug))();
    });

    window.addEventListener('popstate', _setActive);
};

/**
 * Removes all menu groups in the sidebar.
 * @returns void.
 */
const _clearMenuGroups = () =>
    Array.from(
        document.getElementById(SIDEBAR_CLASS.nav)?.children || []
    ).forEach((menuGroup) => menuGroup.remove());

/**
 * Handles the navigation logic for when a user clicks on a route
 * in the side navigation bar.
 *
 * @param {Object} event The click event.
 * @returns {void}
 */
const _navigate = (event) => {
    event.preventDefault();

    // if the token or the user info comes up empty,
    // invalidate this session and redirect home
    // TODO: MA-82 (proper toast on login invalidation)
    if (!isLoggedIn()) {
        window.location.href = '/';
    }

    const url = event.target.getAttribute('href');
    const mountedApps = getMountedApps();
    const isSwitchingApps =
        (mountedApps.includes('@weever/wxPortal') &&
            (url.includes('inspections') ||
                url.includes('databases') ||
                url.includes('form-folders'))) ||
        (mountedApps.includes('inspections') && !url.includes('inspections')) ||
        (mountedApps.includes('@weever/flexDSM') &&
            !url.includes('databases')) ||
        (mountedApps.includes('@weever/formFolders') &&
            !url.includes('form-folders')) ||
        (mountedApps.includes('@weever/siteManager') &&
            !url.includes('site-manager')) ||
        (mountedApps.includes('@weever/opsDashboard') &&
            !url.includes('dashboard'));

    // If the href is a full  http/https url we want to treat that as an absolute path, not a relative one.
    const isExternalUrl = url.match(/^http/);

    if (isSwitchingApps || isExternalUrl) {
        window.location.href = url;
    } else {
        history.pushState({ path: url }, '', url);
    }
};

/**
 * Removes any loading animation in the sidebar.
 * @returns void.
 */
const _removeLoader = () =>
    document.getElementById(SIDEBAR_CLASS.progressLoaderId)?.remove();

/**
 * Displays a loading animation.
 * @returns void.
 */
const _renderLoader = () => {
    if (document.getElementById(SIDEBAR_CLASS.progressLoaderId)) {
        return;
    }

    const sidebarNavigation = document.getElementById(SIDEBAR_CLASS.nav);
    const loader = document.createElement('div');
    loader.setAttribute('id', SIDEBAR_CLASS.progressLoaderId);
    loader.classList.add(SIDEBAR_CLASS.progressLoader);

    const innerLoader = document.createElement('div');
    innerLoader.classList.add(SIDEBAR_CLASS.progressLoaderIndeterminate);
    loader.appendChild(innerLoader);

    sidebarNavigation.insertBefore(loader, sidebarNavigation.firstChild);
};

/**
 * Renders a menu group to the given sidebar.
 *
 * @param {string} menuGroupTitle The title of this menu group.
 * @returns {HTMLUListElement | null} The empty menu group ready to be populated by menu items, or null if the
 * sidebar menu does not exist.
 */
const _renderMenuGroup = (menuGroupTitle, translator) => {
    const sidebarNavigation = document.getElementById(SIDEBAR_CLASS.nav);
    if (!sidebarNavigation) {
        return null;
    }
    const menuBlock = document.createElement('div');
    menuBlock.classList.add(SIDEBAR_CLASS.menu);
    sidebarNavigation.appendChild(menuBlock);

    const title = document.createElement('h4');
    title.classList.add(SIDEBAR_CLASS.title, 'u-text-bold', 'u-text-small');
    title.innerText = translator.translate(menuGroupTitle);
    menuBlock.appendChild(title);

    const unorderedList = document.createElement('ul');
    unorderedList.classList.add(SIDEBAR_CLASS.list, SIDEBAR_CLASS.listv);
    menuBlock.appendChild(unorderedList);

    return unorderedList;
};

/**
 * Renders a menu group item to the given menu group.
 * Attaches event listeners to menu item click events to set active
 * routes and push state.
 *
 * @param {HTMLUListElement} menuGroup The menu group to add the item to.
 * @param {object} item The menu item.
 * @returns {function(item): void} a function that expects to recieve the item to render.
 */
const _renderMenuGroupItem = (menuGroup, translator) => (item) => {
    if (!menuGroup) {
        return null;
    }
    const menuItem = document.createElement('li');
    menuItem.classList.add(SIDEBAR_CLASS.item);
    menuItem.setAttribute('data-test-id', SIDEBAR_CLASS.item);
    menuGroup.appendChild(menuItem);

    const link = document.createElement('a');
    link.setAttribute('href', item.href);
    link.classList.add(SIDEBAR_CLASS.link);

    // todo: orcs-132
    // handle awkward legacy url encoding
    // (still broken when using browser buttons)
    if (item.href.includes('openTickets')) {
        link.setAttribute('id', 'open-tickets-breaks-stuff');
    }

    // initialize the currently active item
    if (item.href === window.location.pathname) {
        link.classList.add('is-active');
    }

    link.innerText = translator.translate(item.title);
    const menuItemIcon = document.createElement('i');
    menuItemIcon.classList.add(SIDEBAR_CLASS.icon, 'wx-icon', item.icon);
    link.prepend(menuItemIcon);

    link.addEventListener('click', _navigate);

    menuItem.appendChild(link);
};

/**
 * Displays an error message in the sidebar.
 * @param {Translator} translator The translation tool object.
 * @param {boolean} error Flag to determine which version of the message to render.
 * @returns void.
 */
const _renderSidebarMessage = (translator, error = false) => {
    const title = translator.translate(
        error ? 'problemRetrievingMenu' : 'noMenuItemsYet'
    );
    const retry = translator.translate('retry');
    let message = translator.translate('pleaseEnsureYouAreConnected');
    if (error) {
        message = `${translator.translate('sorryThereWasAProblem')} ` + message;
    }

    const sidebarPlaceholderTitle = document.getElementById(
        'sidebar-message-title'
    );
    const sidebarPlaceholderText = document.getElementById(
        'sidebar-message-text'
    );
    const sidebarRetryLink = document.getElementById('retry-link');

    if (sidebarPlaceholderText && sidebarPlaceholderTitle && sidebarRetryLink) {
        sidebarPlaceholderTitle.innerText = title;
        sidebarPlaceholderText.innerText = message;
        sidebarRetryLink.innerText = retry;
    }
};

/**
 * Removes the active state from the previous menu item selection,
 * and sets the new item as active. Also handles browser button
 * navigation.
 */
const _setActive = () => {
    if (window.location?.pathname) {
        const oldLink = document.querySelector(
            `${SIDEBAR_CLASS.rootId} .${SIDEBAR_CLASS.link}.is-active`
        );
        oldLink?.classList.remove('is-active');

        let newLink = document.querySelector(
            `${SIDEBAR_CLASS.rootId} .${SIDEBAR_CLASS.link}[href="${window.location.pathname}"]`
        );

        // todo: orcs-132
        // handle awkward legacy url encoding
        // (still broken when using browser buttons)
        if (window.location.pathname.includes('openTickets')) {
            newLink = document.getElementById('open-tickets-breaks-stuff');
        }

        newLink?.classList.add('is-active');
    }
};

/**
 * Creates and appends menu groups and menu items, ordered by menu group.
 * @param {string} appSlug The current app slug.
 * @param {string} appRootUrl The URL of the application root.
 * @returns {Promise<void>}
 */
const _setMenuItems = async (appConfig, appSlug, localesFromDatabase) => {
    const translator = await buildTranslator(appConfig, 'sidebar/locales', localesFromDatabase);
    _renderSidebarMessage(translator);
    try {
        _renderLoader();
        const menuItems = await fetchMenuItems(appSlug);
        const translator = await buildTranslator(appConfig, 'sidebar/locales', localesFromDatabase);

        // TODO: this must be removed when our backend sends back proper locale keys
        const camelize = (str) => {
            return str
                .toLowerCase()
                .replace(/[().\-/]/g, '')
                .replace(/^[a-z]|\b[a-z]|\s+/g, function (match, index) {
                    if (+match === 0) return '';
                    return index === 0
                        ? match.toLowerCase()
                        : match.toUpperCase();
                });
        };
        menuItems?.menuGroupOrder?.forEach((m) => (m = camelize(m)));
        menuItems?.menuItems?.forEach((m) => (m.title = camelize(m.title)));

        _removeLoader();
        if (menuItems.menuGroupOrder) {
            _clearMenuGroups();
        }
        menuItems?.menuGroupOrder?.forEach((menuGroupTitle) => {
            const menuGroup = _renderMenuGroup(menuGroupTitle, translator);
            const relevantItems = menuItems.menuItems.filter(
                (item) => item.menuGroup === menuGroupTitle
            );
            relevantItems.forEach(_renderMenuGroupItem(menuGroup, translator));
        });
    } catch (err) {
        console.error(err);
        _removeLoader();
        _renderSidebarMessage(translator, true);
    }
};

/**
 * Renders the menu items in the side navigation bar.
 * @param {string} appSlug The current app slug.
 * @param {string} appRootUrl The URL of the application root.
 * @return {Promise<void>}
 */
const renderMenuItems = async (appConfig, appSlug, localesFromDatabase) => {
    await _setMenuItems(appConfig, appSlug, localesFromDatabase);
    _addEventHandlers(appConfig, appSlug);
};

export default renderMenuItems;
